import { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { ChevronRight, Search, LogOut, Moon, Sun, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const LABELS = {
  "": "Overview", agencies: "Agencies", clients: "Clients", brands: "Brands",
  warehouses: "Warehouses", marketplaces: "Marketplaces", ai: "AI Insights",
  tenants: "Tenants", plans: "Plans", users: "Team", audit: "Audit Logs",
  settings: "Settings",
};

export default function TopNav() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout, tenant, setTenant } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("zj_theme");
    return saved ? saved === "dark" : true;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("zj_theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    api.get("/tenants").then((r) => {
      setTenants(r.data || []);
      if (!tenant && r.data?.length) setTenant(r.data[0]);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const segs = loc.pathname.split("/").filter(Boolean);
  const crumbs = [{ label: "Home", to: "/dashboard" }, ...segs.map((s, i) => ({
    label: LABELS[s] || s,
    to: "/" + segs.slice(0, i + 1).join("/"),
  }))];

  return (
    <header className="h-14 border-b border-border bg-card flex items-center px-4 gap-4 sticky top-0 z-30" data-testid="app-topnav">
      {/* Tenant switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="tenant-switcher"
            className="flex items-center gap-2 px-3 py-1.5 border border-border hover:bg-secondary transition-colors"
            style={{ borderRadius: 2 }}
          >
            <Building2 className="w-3.5 h-3.5 text-primary" />
            <span className="font-mono text-xs uppercase tracking-wider">
              {tenant?.name || (user?.role === "super_admin" ? "All Tenants" : "No tenant")}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64" data-testid="tenant-switcher-menu">
          <DropdownMenuLabel className="label-mono">Tenants</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {tenants.length === 0 && <DropdownMenuItem disabled>No tenants</DropdownMenuItem>}
          {tenants.map((t) => (
            <DropdownMenuItem
              key={t.id}
              onClick={() => setTenant(t)}
              data-testid={`tenant-option-${t.slug}`}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{t.name}</span>
                <span className="label-mono">/{t.slug} · {t.country}</span>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground overflow-hidden" data-testid="breadcrumbs">
        {crumbs.map((c, i) => (
          <span key={`${c.to}-${i}`} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3 h-3" />}
            <Link to={c.to} className="hover:text-foreground transition-colors truncate max-w-[160px]">
              {c.label}
            </Link>
          </span>
        ))}
      </nav>

      <div className="ml-auto flex items-center gap-2">
        <div className="hidden md:flex items-center gap-2 px-2.5 py-1.5 border border-border" style={{ borderRadius: 2 }}>
          <Search className="w-3.5 h-3.5 text-muted-foreground" />
          <input
            data-testid="global-search"
            className="bg-transparent outline-none text-xs w-48 font-mono"
            placeholder="Search…"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setDark((d) => !d)}
          data-testid="theme-toggle"
          aria-label="Toggle theme"
        >
          {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu"
              className="flex items-center gap-2 px-2 py-1 border border-border hover:bg-secondary transition-colors"
              style={{ borderRadius: 2 }}
            >
              <div className="w-6 h-6 bg-primary/15 grid place-items-center text-primary font-mono text-[10px]" style={{ borderRadius: 2 }}>
                {(user?.name || "?").slice(0, 2).toUpperCase()}
              </div>
              <span className="text-xs font-medium">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>
              <div className="text-xs">{user?.email}</div>
              <div className="mt-1"><span className="role-badge">{user?.role}</span></div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">
              Profile & Security
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="menu-logout">
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
